import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { profileAPI, friendsAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { User, Award, Flame, UserPlus, UserMinus, Loader2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await profileAPI.player(id);
      setPlayer(res.data);
    } catch {
      toast.error('Player not found');
      navigate(-1);
    }
    setLoading(false);
  };

  const toggleFriend = async () => {
    if (!player) return;
    setToggling(true);
    try {
      if (player.is_friend) {
        await friendsAPI.remove(player.id);
        toast.success('Friend removed');
      } else {
        await friendsAPI.add(player.id);
        toast.success('Friend added!');
      }
      setPlayer(p => ({ ...p, is_friend: !p.is_friend }));
    } catch { toast.error('Failed'); }
    setToggling(false);
  };

  if (loading) {
    return (
      <Layout title="PLAYER" showBack>
        <div className="flex justify-center py-24"><div className="spinner" /></div>
      </Layout>
    );
  }

  if (!player) return null;

  const headshot = player.headshot_url ? `${API_URL}${player.headshot_url}` : null;
  const badges = player.badges || [];

  return (
    <Layout title="PLAYER PROFILE" showBack>
      <div className="px-4 py-6 space-y-5" data-testid="public-profile">
        {/* Avatar + Name */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-3">
          <div className="w-24 h-24 rounded-2xl bg-white border-2 border-[#E2E8F0] overflow-hidden flex items-center justify-center" data-testid="public-headshot-area">
            {headshot ? (
              <img src={headshot} alt={player.name} className="w-full h-full object-cover" data-testid="public-headshot-img" />
            ) : (
              <User size={40} className="text-[#1C2B54]" />
            )}
          </div>
          <div className="text-center">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold" data-testid="public-profile-name">{player.name}</h2>
            <div className="flex items-center justify-center gap-2 mt-1">
              {player.position && (
                <span className="text-[10px] bg-[#00E5FF]/15 text-[#00E5FF] px-2 py-0.5 rounded-full font-bold uppercase" data-testid="public-position">{player.position}</span>
              )}
              {player.shooting_hand && (
                <span className="text-[10px] bg-[#A78BFA]/15 text-[#A78BFA] px-2 py-0.5 rounded-full font-bold uppercase" data-testid="public-hand">{player.shooting_hand} Shot</span>
              )}
            </div>
            {player.current_team && (
              <p className="text-[10px] text-[#64748B] mt-1 uppercase tracking-widest" data-testid="public-team">{player.current_team}</p>
            )}
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="flex gap-3"
        >
          <div className="flex-1 bg-white border border-[#E2E8F0] rounded-xl p-4 text-center">
            <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#2563EB]" data-testid="public-weekly-score">{player.weekly_score}%</p>
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold mt-1">Weekly Score</p>
          </div>
          <div className="flex-1 bg-white border border-[#E2E8F0] rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-1">
              <Flame size={18} className="text-[#FF9100]" />
              <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#FF9100]" data-testid="public-streak">{player.streak}</p>
            </div>
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold mt-1">Day Streak</p>
          </div>
        </motion.div>

        {/* Badges */}
        {badges.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          >
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold mb-3">Badges Earned</p>
            <div className="flex gap-3 flex-wrap" data-testid="public-badges">
              {badges.map(b => (
                <div key={b.name} className="flex flex-col items-center gap-1">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center border-2" style={{ borderColor: b.color, background: `${b.color}15` }}>
                    <Award size={22} style={{ color: b.color }} />
                  </div>
                  <span className="text-[9px] font-bold" style={{ color: b.color }}>{b.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Add/Remove Friend Button */}
        {!player.is_you && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <button
              onClick={toggleFriend}
              disabled={toggling}
              className={`w-full font-bold uppercase tracking-wide rounded-xl py-3.5 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50 ${
                player.is_friend
                  ? 'bg-white border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10'
                  : 'bg-[#2563EB] hover:bg-[#3B82F6] text-white'
              }`}
              data-testid="toggle-friend-btn"
            >
              {toggling ? (
                <Loader2 size={16} className="animate-spin" />
              ) : player.is_friend ? (
                <><UserMinus size={16} /> Remove Friend</>
              ) : (
                <><UserPlus size={16} /> Add Friend</>
              )}
            </button>
          </motion.div>
        )}

        {/* If it's your own profile, link to edit */}
        {player.is_you && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <button
              onClick={() => navigate('/profile')}
              className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-xl py-3.5 transition-colors text-sm"
              data-testid="edit-own-profile-btn"
            >
              Edit Your Profile
            </button>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
